/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 28th 2024, 5:00:24 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useSearchParams } from "react-router-dom";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { ToolBarItemButton } from "common/components/toolbar";
import { useGlobalScope } from "core/providers";
import { MediaAssetType } from "lib/schemas";
import { useMediaLibraryAPI } from "media-library/services/MediaLibraryAPI";
import { useMediaLibraryStore, mediaLibraryStoreSelectors } from "media-library/store";
import { APIListProgressWrapper } from "ui-widgets";
import { LibraryFiles } from "./LibraryFiles";
import { LibraryImages } from "./LibraryImages";
import { TOnChooseMediaAsset } from "./types";

type MediaLibraryComponentProps = {
  onChooseAsset?: TOnChooseMediaAsset;
};

export const MediaLibraryComponent = ({ onChooseAsset }: MediaLibraryComponentProps) => {
  const [searchParams, setSearchParams] = useSearchParams({ asset_type: "image" });
  const asset_type: MediaAssetType = searchParams.get("asset_type") as MediaAssetType;
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();
  const { useList } = useMediaLibraryAPI();
  const {
    makeRequest: fetchLibraries,
    isMakingAPIRequest: isFetchingLibraries,
    isAPIError: fetchLibrariesHasError,
    httpResponseStatusCode: fetchLibrariesStatusCode,
  } = useList();

  const { populateMediaLibraries } = useMediaLibraryStore();
  const mediaLibraries = useMediaLibraryStore(mediaLibraryStoreSelectors.getLibraries(selectedOrganization?.id));

  React.useEffect(() => {
    if (mediaLibraries.length) return;

    fetchLibraries({ setValidAPIResponse: populateMediaLibraries });
  }, [fetchLibraries, populateMediaLibraries, mediaLibraries.length]);

  const libraryState = mediaLibraries?.[0];

  const showImages = asset_type === "image" && libraryState?.mediaLibrary?.id;
  const showFiles = asset_type === "file" && libraryState?.mediaLibrary?.id;

  return (
    <APIListProgressWrapper
      showLoader={isFetchingLibraries}
      isAPIError={fetchLibrariesHasError}
      items={mediaLibraries}
      httpResponseStatusCode={fetchLibrariesStatusCode}
    >
      <Stack direction="row" columnGap={4}>
        <Box sx={{ flexBasis: "15%", borderRight: "1px solid #7b00b10d", minHeight: "100vh" }}>
          <ListItemText
            primary={`Media Library`}
            primaryTypographyProps={{
              color: "text.secondary",
              fontWeight: "medium",
              lineHeight: "20px",
            }}
            sx={{
              pt: 1,
              pb: 1,
            }}
          />
          <div onClick={() => setSearchParams({ asset_type: "image" })}>
            <ToolBarItemButton
              tooltipText={<Typography>Manage Images</Typography>}
              buttonIcon={<ImageIcon color="secondary" />}
              buttonTitle={<>Images</>}
              buttonCursor="pointer"
              buttonProps={{ selected: asset_type === "image" ? true : false }}
            />
          </div>
          <div onClick={() => setSearchParams({ asset_type: "file" })}>
            <ToolBarItemButton
              tooltipText={<Typography>Manage Files</Typography>}
              buttonIcon={<InsertDriveFileIcon color="secondary" />}
              buttonTitle={<>Files</>}
              buttonCursor="pointer"
              buttonProps={{ selected: asset_type === "file" ? true : false }}
            />
          </div>
        </Box>
        <Box sx={{ flexBasis: "85%", paddingBottom: 5, paddingRight: 5, paddingTop: 1 }}>
          {showImages ? (
            <LibraryImages mediaLibraryId={libraryState?.mediaLibrary?.id} onChooseAsset={onChooseAsset} />
          ) : null}
          {showFiles ? (
            <LibraryFiles mediaLibraryId={libraryState?.mediaLibrary?.id} onChooseAsset={onChooseAsset} />
          ) : null}
        </Box>
      </Stack>
    </APIListProgressWrapper>
  );
};
